import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, Fragment } from "react";
import { List, CellMeasurer, CellMeasurerCache } from "react-virtualized";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Drawer from "@mui/material/Drawer";

import { AlertProfile } from "orion-components/Profiles";
import { AlertPanelDetailWidget } from "orion-components/Profiles/Widgets";
import EventBus from "orion-components/SharedComponents/EventBus";
import $ from "jquery";
import { setMapOffsetExternal } from "../../../AppState/Actions";
import { openAlertProfile, closeAlertProfile } from "orion-components/Profiles/AlertProfile/actions";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import { initComponentInstance, updateComponentInstance } from "../../../SharedComponents/ComponentState/redux/actions";

const propTypes = {
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object,
	notifications: PropTypes.array.isRequired,
	animating: PropTypes.bool.isRequired,
	archive: PropTypes.bool.isRequired,
	fetchNextPage: PropTypes.func.isRequired,
	fetching: PropTypes.bool.isRequired,
	shouldFetch: PropTypes.bool.isRequired,
	componentState: PropTypes.object.isRequired,
	readOnly: PropTypes.bool.isRequired,
	forReplay: PropTypes.bool.isRequired,
	removeDockedCameraAndState: PropTypes.func.isRequired,
	endDate: PropTypes.string,
	dir: PropTypes.string.isRequired,
	filterStatus: PropTypes.bool.isRequired
};

const defaultProps = {
	selectFloorPlanOn: null,
	floorPlansWithFacilityFeed: null
};

const cache = new CellMeasurerCache({
	fixedWidth: true,
	defaultHeight: 140
});

const replayStyles = {
	overflow: "hidden",
	height: "100%",
	position: "relative",
	display: "flex",
	flexDirection: "column",
	minHeight: 0,
	width: "100%"
};

const COMPONENT_KEY = "Dock.Notifications.TimeNotificationGroup";

const TimeNotificationGroup = props => {
	const dispatch = useDispatch();

	const { alertPanelLayout } = useSelector(state => state.appState?.persisted);
	const alertProfileSlideout = alertPanelLayout === "two-column";
	const expandedAlert = useSelector(state => state.appState.dock.dockData.expandedAlert);
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));

	const {
		notifications,
		animating,
		archive,
		fetchNextPage,
		fetching,
		shouldFetch,
		componentState,
		readOnly,
		forReplay,
		removeDockedCameraAndState,
		endDate,
		selectFloorPlanOn,
		floorPlansWithFacilityFeed,
		dir,
		filterStatus
	} = props;
	const [animatingState, setAnimatingState] = useState(false);
	const [nextNotifications, setNextNotifications] = useState(notifications);
	const [nextAnimating, setNextAnimating] = useState(animating);
	const [wrapper, setWrapper] = useState(null);

	const [detailView, setDetailView] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState({});
	const [tempSelectedNotification, setTempSelectedNotification] = useState({});

	const latestSelectedNotificationId = useRef(selectedNotification?.id);

	const primaryWidth =
		dir && dir == "rtl" ? $(".dock-controlRTL").outerWidth() / 2 : $(".dock-control").outerWidth() / 2;
	const secondaryWidth = $(".alert-panel-slideout").length ? $(".alert-panel-slideout").outerWidth() / 2 : 0;

	// later on we can replace eventBus with the following
	const openAlertFromComponentState = useSelector(
		state => state.componentState?.[COMPONENT_KEY]?.default?.openAlertProfile
	);

	useEffect(() => {
		const existingInstance = componentState?.[COMPONENT_KEY]?.default;
		if (!existingInstance) {
			dispatch(
				initComponentInstance(COMPONENT_KEY, "default", {
					openAlertProfile: null
				})
			);
		}
	}, []);

	useEffect(() => {
		if (openAlertFromComponentState) {
			openExternalNotification(openAlertFromComponentState);
			dispatch(updateComponentInstance(COMPONENT_KEY, "default", { openAlertProfile: null }));
		}
	}, [openAlertFromComponentState]);

	let virtualList = null;
	const virtualListRef = useRef(null);

	const usePrevious = value => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		}, [value]);
		return ref.current;
	};

	useEffect(() => {
		// Clear the cache when notifications change
		cache.clearAll();
		// Force the List to re-render and re-measure rows
		if (virtualListRef.current) {
			virtualListRef.current.recomputeRowHeights();
		}
	}, [notifications]);

	useEffect(() => {
		const handleResize = () => {
			cache.clearAll();
			if (virtualListRef.current) {
				virtualListRef.current.recomputeRowHeights();
				virtualListRef.current.forceUpdateGrid && virtualListRef.current.forceUpdateGrid();
			}
			// Update wrapper in case its size changed
			setWrapper(document.getElementById("notification-tab-wrapper"));
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (notifications.length !== nextNotifications.length) {
			setNextNotifications(notifications);
		}
		if (nextAnimating !== animating) {
			setNextAnimating(animating);
		}
	}, [notifications, animating]);

	useEffect(() => {
		if (nextNotifications.length > notifications.length) {
			setAnimatingState(false);
		} else if (nextAnimating !== animating) {
			setAnimatingState(archive ? "left" : "right");
		}
	}, [props]);

	const openExternalNotification = notification => {
		dispatch(openAlertProfile(notification, forReplay));
		viewAlertPanelDetail(notification);
	};

	useEffect(() => {
		if (archive) {
			handleScroll();
		}
		EventBus.subscribe("openAlertProfile", openExternalNotification);
		return () => {
			if (latestSelectedNotificationId.current) closeDetailView();
			dispatch(setMapOffsetExternal(primaryWidth));
			EventBus.unsubscribe("openAlertProfile", openExternalNotification);
		};
	}, []);

	useEffect(() => {
		latestSelectedNotificationId.current = selectedNotification?.id;
	}, [selectedNotification]);

	const prevProps = usePrevious(props);

	useEffect(() => {
		const index = notifications.findIndex(notification => notification.id === expandedAlert);
		if (prevProps && virtualListRef.current && expandedAlert !== prevProps.expandedAlert && index > -1) {
			const offset = virtualListRef.current.getOffsetForRow({
				alignment: "center",
				index
			});
			virtualListRef.current.scrollToPosition(offset);
		}
	}, [props]);

	const handleScroll = () => {
		const element = document.getElementById("virtualized-notification-list");
		if (element) {
			element.addEventListener("scroll", event => {
				const element = event.target;
				const { scrollTop, clientHeight, scrollHeight } = element;
				if (
					Math.floor(scrollHeight - scrollTop) <= clientHeight && // --> If we scroll to bottom
					!fetching && // --> AND not already fetching
					shouldFetch
				) {
					// --> AND we haven't fetched the same data in the past X seconds
					fetchNextPage();
				}
			});
		}
	};

	useEffect(() => {
		if (
			expandedAlert &&
			expandedAlert === tempSelectedNotification.id &&
			selectedNotification?.id !== tempSelectedNotification.id
		) {
			setSelectedNotification(tempSelectedNotification);
			setDetailView(true);
		}
	}, [expandedAlert, tempSelectedNotification]);

	const viewAlertPanelDetail = useCallback(data => {
		if (tempSelectedNotification.id === data.id) return; // validating if selected notification is being reselected, to avoid unnecessary rerenders in alert list.
		setTempSelectedNotification(data);
	}, []);

	const closeDetailView = () => {
		setDetailView(false);
		dispatch(closeAlertProfile(latestSelectedNotificationId.current));
		setSelectedNotification({});
		dispatch(closeAlertProfile(selectedNotification.id));
	};

	useLayoutEffect(() => {
		setWrapper(document.getElementById("notification-tab-wrapper"));
	}, []);

	const rowRenderer = useCallback(
		({ index, key, parent, style }) => {
			const notification = notifications[index];
			if (notification.isGroupHeader) {
				return (
					<CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
						{({ registerChild }) => (
							<div
								id={notification.id}
								ref={registerChild}
								style={{ ...style, height: "auto", color: "#fff" }}
							>
								{notification.groupName}
							</div>
						)}
					</CellMeasurer>
				);
			} else {
				return (
					<CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
						{({ registerChild }) => (
							<div ref={registerChild} style={style}>
								<AlertProfile
									id={notification.id}
									readOnly={readOnly}
									notificationId={forReplay ? notification.id : null}
									notification={forReplay ? notification : null}
									activityId={notification.activityId}
									index={index}
									removeDockedCameraAndState={removeDockedCameraAndState}
									forReplay={forReplay}
									endDate={endDate}
									selectFloorPlanOn={selectFloorPlanOn}
									floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
									viewAlertPanelDetail={viewAlertPanelDetail}
									selectedNotificationId={selectedNotification?.id}
								/>
							</div>
						)}
					</CellMeasurer>
				);
			}
		},
		[
			notifications,
			readOnly,
			forReplay,
			removeDockedCameraAndState,
			endDate,
			selectFloorPlanOn,
			floorPlansWithFacilityFeed,
			viewAlertPanelDetail,
			selectedNotification?.id
		]
	);

	let hasError = false;
	if (notifications[0]) {
		hasError = componentState.rejectedNots.includes(notifications[0].id);
	}

	useEffect(() => {
		dispatch(setMapOffsetExternal(-secondaryWidth));
	}, [detailView]);

	return (
		<>
			<div
				style={forReplay ? replayStyles : {}}
				className={
					animatingState && !hasError
						? "notification-group animating animating-" + animatingState
						: "notification-group"
				}
			>
				{wrapper && !filterStatus && !(forReplay && detailView) && (
					<List
						id="virtualized-notification-list"
						deferredMeasurementCache={cache}
						height={wrapper.offsetHeight - 76}
						rowCount={notifications.length}
						rowHeight={cache.rowHeight}
						rowRenderer={rowRenderer}
						width={wrapper.offsetWidth - (scrollBarsEnabled ? 13 : 26)}
						ref={virtualListRef}
					/>
				)}
				{forReplay && selectedNotification?.id && detailView && (
					<div
						style={{
							position: "absolute",
							inset: 0,
							height: "100%",
							width: "100%",
							overflow: "hidden",
							backgroundColor: "#2C2D2F",
							zIndex: 1,
							display: "flex",
							flexDirection: "column",
							alignItems: "stretch",
							minHeight: 0
						}}
					>
						<AlertPanelDetailWidget
							key={selectedNotification.id}
							readOnly={readOnly}
							notificationId={selectedNotification.id}
							notification={selectedNotification}
							activityId={selectedNotification.activityId}
							endDate={endDate}
							closeDetailView={closeDetailView}
							selectFloorPlanOn={selectFloorPlanOn}
							floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
							dir={dir}
							forReplay={forReplay}
						/>
					</div>
				)}

				{!forReplay && !alertProfileSlideout && selectedNotification?.id && detailView && (
					<Drawer open={detailView} anchor={dir === "rtl" ? "left" : "right"} variant="persistent">
						<AlertPanelDetailWidget
							key={selectedNotification.id}
							readOnly={readOnly}
							notificationId={selectedNotification.id}
							notification={selectedNotification}
							activityId={selectedNotification.activityId}
							endDate={endDate}
							closeDetailView={closeDetailView}
							selectFloorPlanOn={selectFloorPlanOn}
							floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
							dir={dir}
							forReplay={forReplay}
						/>
					</Drawer>
				)}
			</div>
			{alertProfileSlideout &&
				!forReplay && ( // rendered the new slide panel as a separate block, instead of placing inside notification-group block to resolve drawer animation issues.
					<Drawer
						open={detailView}
						anchor={dir === "rtl" ? "left" : "right"}
						variant="persistent"
					PaperProps={{
						className: "alert-panel-slideout",
						style: {
							border: "unset",
							backgroundColor: "transparent",
							position: "fixed",
							top: "48px",
							height: "calc(100vh - 48px)",
							overflow: "visible",
							zIndex: 599,
							...(dir === "rtl" && { left: 420, boxShadow: "5px 0 6px #0000008b" }),
							...(dir === "ltr" && { right: 420, boxShadow: "-5px 0 6px #0000008b" })
						}
					}}
					>
						{/* Avoid wrapping `detailView` around `Drawer` to prevent interference with the drawer's animation. */}
						{detailView &&
							selectedNotification?.id &&
							selectedNotification?.id === tempSelectedNotification.id && (
								<AlertPanelDetailWidget
									key={selectedNotification.id}
									readOnly={readOnly}
									notificationId={selectedNotification.id}
									notification={selectedNotification}
									activityId={selectedNotification.activityId}
									endDate={endDate}
									closeDetailView={closeDetailView}
									selectFloorPlanOn={selectFloorPlanOn}
									floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
									dir={dir}
									alertProfileSlideout={alertProfileSlideout}
									forReplay={forReplay}
								/>
							)}
					</Drawer>
				)}
		</>
	);
};

TimeNotificationGroup.propTypes = propTypes;
TimeNotificationGroup.defaultProps = defaultProps;

export default TimeNotificationGroup;
